import React, { useEffect, useRef, useState } from 'react';
import { User } from 'firebase/auth';
import { sendVerificationEmail, reloadCurrentUser, signOutUser } from '../../services/auth';
import HandoffShell, { HandoffLink } from './HandoffShell';

interface VerifyEmailNoticeProps {
    user: User;
}

function VerifyEmailNotice({ user }: VerifyEmailNoticeProps) {
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const [isResending, setIsResending] = useState(false);

    // Send the verification email once when the gate first appears — covers both
    // a fresh signup and an existing unverified user logging in. Ref-guarded so
    // React 18 StrictMode's double-mount (dev only) doesn't fire two sends.
    const didSendRef = useRef(false);
    useEffect(() => {
        if (didSendRef.current) return;
        didSendRef.current = true;
        sendVerificationEmail(user).catch((err) => {
            console.error('Failed to send verification email:', err);
        });
    }, [user]);

    const handleCheck = async () => {
        setIsChecking(true);
        setError(null);
        setInfo(null);

        try {
            const verified = await reloadCurrentUser();
            if (verified) {
                // emailVerified is cached in the ID token; reload it fresh so the
                // whole app re-bootstraps with a verified session and the gate opens.
                window.location.reload();
            } else {
                setError("We couldn't confirm your email yet. Click the link we sent, then try again.");
            }
        } catch (err: any) {
            setError(err.message || 'Failed to check verification status');
        } finally {
            setIsChecking(false);
        }
    };

    const handleResend = async () => {
        setIsResending(true);
        setError(null);
        setInfo(null);

        try {
            await sendVerificationEmail(user);
            setInfo('Verification email sent. Check your inbox and spam folder.');
        } catch (err: any) {
            setError(err.message || 'Failed to resend verification email');
        } finally {
            setIsResending(false);
        }
    };

    return (
        <HandoffShell
            label="Check your inbox"
            detail={`We sent a verification link to ${user.email}`}
            action={<HandoffLink onClick={() => signOutUser()}>Sign out</HandoffLink>}
        >
            <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="text-[13px] text-[rgba(42,40,36,0.55)] underline transition-colors hover:text-ink-secondary disabled:opacity-50"
            >
                {isResending ? 'Sending…' : 'Resend the email'}
            </button>

            {(info || error) && (
                <p className={`text-meta font-semibold ${error ? 'text-alert' : 'text-[rgba(42,40,36,0.5)]'}`}>
                    {error || info}
                </p>
            )}

            <button
                type="button"
                onClick={handleCheck}
                disabled={isChecking}
                className="border-b border-[rgba(42,40,36,0.4)] pb-0.5 text-[13px] font-semibold text-ink-secondary transition-colors hover:text-ink disabled:opacity-50"
            >
                {isChecking ? 'Checking…' : "I've verified my email"}
            </button>
        </HandoffShell>
    );
}

export default VerifyEmailNotice;
